// A device privacy screen, separate from Firebase authentication and encryption.
const PREFIX = 'tradelog-device-lock-v1';
const ITERATIONS = 210000;
export const MAX_PIN_ATTEMPTS = 5;
const key = (uid, part) => `${PREFIX}:${uid}:${part}`;
const store = supplied => {
  try {
    const storage = supplied || window.localStorage;
    if (!storage || !['getItem', 'setItem', 'removeItem'].every(method => typeof storage[method] === 'function')) throw new Error('Storage unavailable');
    return storage;
  } catch { throw new Error('This browser cannot save device-lock settings. Enable local storage before setting a PIN.'); }
};
export const validPin = pin => /^\d{6}$/.test(pin || '');
const hex = bytes => Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
const bytes = value => Uint8Array.from(value.match(/.{2}/g), h => parseInt(h, 16));
const readLockVersion = (uid, storage) => ({ marker: storage.getItem(key(uid, 'locked')), generation: storage.getItem(key(uid, 'generation')) });
const markerIsLocked = marker => marker === '1' || marker?.startsWith('1:') === true;
export function readLock(uid, supplied) {
  if (!uid) return { configured: false, locked: false };
  let storage, locked;
  try { storage = store(supplied); locked = markerIsLocked(storage.getItem(key(uid, 'locked'))); }
  catch { return { configured: false, locked: false, unavailable: true }; }
  try {
    const config = JSON.parse(storage.getItem(key(uid, 'verifier')) || 'null');
    const configured = config?.version === 1 && config.iterations === ITERATIONS && /^[a-f0-9]{32}$/.test(config.salt) && /^[a-f0-9]{64}$/.test(config.hash);
    return { configured: !!configured, locked, config: configured ? config : null, signOutRequired: locked && readAttempts(uid, storage).signOutRequired };
  } catch { return { configured: false, locked, config: null }; }
}
async function digest(pin, salt, cryptoProvider = window.crypto) {
  if (!cryptoProvider?.subtle || !cryptoProvider.getRandomValues) throw new Error('Device locking requires a secure browser with Web Crypto support.');
  const material = await cryptoProvider.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  return hex(await cryptoProvider.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: bytes(salt), iterations: ITERATIONS }, material, 256));
}
export async function configureLock(uid, pin, supplied, cryptoProvider = window.crypto) {
  const storage = store(supplied);
  if (!uid || !validPin(pin)) throw new Error('Choose a six-digit PIN.');
  if (readLock(uid, storage).configured) throw new Error('This device already has a PIN. Unlock it before changing your settings.');
  if (!cryptoProvider?.getRandomValues) throw new Error('Device locking requires Web Crypto support.');
  const salt = hex(cryptoProvider.getRandomValues(new Uint8Array(16)));
  const hash = await digest(pin, salt, cryptoProvider);
  storage.setItem(key(uid, 'verifier'), JSON.stringify({ version: 1, salt, hash, iterations: ITERATIONS }));
  storage.removeItem(key(uid, 'attempts'));
}
export function lockJournal(uid, supplied, cryptoProvider = window.crypto) {
  const storage = store(supplied);
  if (!readLock(uid, storage).configured) throw new Error('Set up a PIN before locking your journal.');
  if (!cryptoProvider?.getRandomValues) throw new Error('Device locking requires Web Crypto support.');
  const generation = hex(cryptoProvider.getRandomValues(new Uint8Array(16)));
  // The marker itself changes first, so a failed second write still leaves a newer, active lock.
  storage.setItem(key(uid, 'locked'), `1:${generation}`);
  storage.setItem(key(uid, 'generation'), generation);
}
export function lockWait(uid, supplied, now = Date.now()) {
  const storage = store(supplied);
  return Math.max(0, readAttempts(uid, storage).until - now);
}
function readAttempts(uid, storage) {
  const raw = storage.getItem(key(uid, 'attempts'));
  if (raw === null) return { failures: 0, until: 0, signOutRequired: false };
  try {
    const record = JSON.parse(raw);
    const failures = Number(record?.failures), until = Number(record?.until);
    if (!record || !Number.isInteger(failures) || failures < 0 || !Number.isFinite(until) || until < 0 || (record.signOutRequired !== undefined && typeof record.signOutRequired !== 'boolean')) throw new Error('Invalid attempts');
    return { failures: Math.min(20, failures), until, signOutRequired: record.signOutRequired ?? failures >= MAX_PIN_ATTEMPTS };
  } catch { return { failures: MAX_PIN_ATTEMPTS, until: 0, signOutRequired: true }; }
}
function signOutError(message = 'Five incorrect PIN attempts. Signing you out to protect your workspace.') {
  const error = new Error(message);
  error.code = 'device-lock/sign-out-required';
  error.signOutRequired = true;
  return error;
}
export function completeLockSignOut(uid, supplied) {
  const storage = store(supplied);
  const attempts = readAttempts(uid, storage);
  storage.setItem(key(uid, 'attempts'), JSON.stringify({ ...attempts, signOutRequired: false }));
}
export async function unlockJournal(uid, pin, supplied, cryptoProvider = window.crypto, now = Date.now(), canComplete = () => true) {
  let storage, attempts;
  try { storage = store(supplied); attempts = readAttempts(uid, storage); }
  catch { throw signOutError('This browser cannot read device-lock settings. Signing you out to keep your workspace private.'); }
  if (attempts.signOutRequired) throw signOutError();
  const wait = Math.max(0, attempts.until - now);
  if (wait) throw new Error(`Try again in ${Math.ceil(wait / 1000)} seconds.`);
  const { config } = readLock(uid, storage);
  if (!config) throw new Error('This device PIN is unavailable. Sign out and reset it to continue.');
  if (!validPin(pin)) throw new Error('Enter your six-digit PIN.');
  const version = readLockVersion(uid, storage);
  const attempt = await digest(pin, config.salt, cryptoProvider);
  if (!canComplete()) throw new Error('Your signed-in account changed.');
  const current = readLock(uid, storage).config;
  if (current?.salt !== config.salt || current?.hash !== config.hash) throw new Error('Your device lock changed. Enter your PIN again.');
  const latestVersion = readLockVersion(uid, storage);
  if (latestVersion.marker !== version.marker || latestVersion.generation !== version.generation) throw new Error('Your workspace was locked again. Try again with your PIN.');
  try { attempts = readAttempts(uid, storage); }
  catch { throw signOutError('This browser cannot read device-lock settings. Signing you out to keep your workspace private.'); }
  if (attempts.signOutRequired) throw signOutError();
  let difference = 0;
  for (let i = 0; i < attempt.length; i++) difference |= attempt.charCodeAt(i) ^ config.hash.charCodeAt(i);
  if (difference) {
    const failures = Math.min(20, attempts.failures + 1);
    const signOutRequired = failures >= MAX_PIN_ATTEMPTS;
    const cooldown = signOutRequired ? Math.min(300000, 30000 * 2 ** (failures - MAX_PIN_ATTEMPTS)) : 0;
    try { storage.setItem(key(uid, 'attempts'), JSON.stringify({ failures, until: now + cooldown, signOutRequired })); }
    catch { throw signOutError('This browser cannot save PIN attempts. Signing you out to keep your workspace private.'); }
    if (signOutRequired) throw signOutError();
    const remaining = MAX_PIN_ATTEMPTS - failures;
    throw new Error(`Incorrect PIN. Try again. ${remaining} ${remaining === 1 ? 'attempt' : 'attempts'} remaining.`);
  }
  storage.removeItem(key(uid, 'attempts'));
  storage.removeItem(key(uid, 'locked'));
  return true;
}
export function resetDeviceLock(uid, supplied) {
  const storage = store(supplied);
  ['verifier', 'locked', 'generation', 'attempts'].forEach(part => storage.removeItem(key(uid, part)));
}
export const isLockStorageKey = (uid, value) => value === null || value?.startsWith(`${PREFIX}:${uid}:`);
